import React, { useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  FaCalendarAlt,
  FaClock,
  FaUserTie,
  FaCalendar,
  FaLinkedin,
  FaTwitter,
  FaFacebook,
  FaLink,
  FaArrowRight
} from 'react-icons/fa'
import { openModal } from '../../../../redux/slices/modalSlice.js';

import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppDispatch.js'
import { fetchWebinarBySlug } from '../../../../redux/slices/webinarsSlice.js'
import SEO from '../../../../components/seo/SEO.jsx'
import { formatDateTime, isFutureDate } from '../../../../utils/formatDate.js'
import { LazyImage } from '../../../../components/common/LazyImage.jsx'
import ErrorBlock from '../../../../components/common/ErrorBlock.jsx'
import SkeletonSection from '../../../../components/common/SkeletonSection.jsx'

import './WebinarDetailPage.scss'

export default function WebinarDetailPage() {
  const { slug } = useParams()
  const dispatch = useAppDispatch()
  const { currentWebinar: webinar, loading, error } = useAppSelector((state) => state.webinars)

  useEffect(() => {
    if (slug) {
      dispatch(fetchWebinarBySlug(slug))
    }
  }, [dispatch, slug])

  const handleOpenContactModal = () => {
    dispatch(openModal({
      modalType: 'contact',
      modalProps: {}
    }));
  };

  if (loading) {
    return (
      <>
        <SEO title="Loading Webinar..." />
        <div className="space-y-12">
          <SkeletonSection />
          <SkeletonSection />
        </div>
      </>
    )
  }

  if (error || !webinar) {
    return (
      <>
        <SEO title="Webinar Not Found" />
        <div className="container-custom section-padding">
          <ErrorBlock
            title="Webinar Not Found"
            message="The webinar you're looking for doesn't exist or has been removed."
            onRetry={() => slug && dispatch(fetchWebinarBySlug(slug))}
          />
        </div>
      </>
    )
  }

  const hero = webinar.hero || {}
  const about = webinar.about || {}
  const agenda = webinar.agenda || {}
  const keyTakeAways = webinar.keyTakeAways || {}
  const speaker = webinar.speaker || {}
  const bottomSection = webinar.bottomSection || {}
  const relatedConfig = webinar.RelatedWebinars || {}
  const related = Array.isArray(webinar.relatedWebinars) ? webinar.relatedWebinars : []

  const isUpcoming = isFutureDate(webinar.dateTime)

  // HERO detail chips
  const detailChips = []

  if (webinar.dateTime) {
    detailChips.push({
      icon: <FaCalendarAlt />,
      text: formatDateTime(webinar.dateTime)
    })
  }

  if (speaker.name) {
    detailChips.push({
      icon: <FaUserTie />,
      text: speaker.name
    })
  }

  if (speaker.companyPosition) {
    detailChips.push({
      icon: <FaClock />,
      text: speaker.companyPosition
    })
  }

  const agendaItems = Array.isArray(agenda.agendaArray) ? agenda.agendaArray : []
  const takeawaysList = Array.isArray(keyTakeAways.cards) ? keyTakeAways.cards : []
  const speakerSocialLinks = Array.isArray(speaker.socialLinks) ? speaker.socialLinks : []

  const getSocialIcon = (iconKey) => {
    if (!iconKey) return <FaLink />
    switch (iconKey.toLowerCase()) {
      case 'linkedin':
        return <FaLinkedin />
      case 'twitter':
        return <FaTwitter />
      case 'facebook':
        return <FaFacebook />
      default:
        return <FaLink />
    }
  }

  const registerHref = hero.buttonLink || webinar.registrationUrl || '#'
  const showRegisterButton = isUpcoming && (hero.buttonText || webinar.registrationUrl)

  return (
    <>
      <SEO
        title={webinar.title}
        description={about?.subheading || webinar.shortDescription}
        image={hero?.imageUrl || webinar.imageUrl}
        type="article"
        publishedTime={webinar.createdAt}
        modifiedTime={webinar.updatedAt}
        author={speaker?.name}
        tags={webinar.tags}
      />

      <article className="webinar-detail-page">
        {/* HERO – v1 style, v2 data */}
        {hero.isVisible !== false && (
          <section className="webinar-detail-hero">
            <div className="webinar-detail-container">
              <div className="webinar-content">
                <div className="webinar-header">
                  {/* Title */}
                  <motion.h1
                    className="webinar-title"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                  >
                    {hero.heading || webinar.title}
                  </motion.h1>

                  {/* Detail chips, description and CTA */}
                  <div className="webinar-header-right">
                    {detailChips.length > 0 && (
                      <motion.div
                        className="webinar-details"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                      >
                        {detailChips.map((detail, index) => (
                          <motion.div
                            key={`${detail.text}-${index}`}
                            className="detail-item"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                          >
                            <span className="detail-icon">{detail.icon}</span>
                            <span className="detail-text">{detail.text}</span>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}

                    {/* Description */}
                    {(hero.subheading || webinar.shortDescription) && (
                      <motion.div
                        className="webinar-description"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                      >
                        <p>{hero.subheading || webinar.shortDescription}</p>
                      </motion.div>
                    )}

                    {/* Register button */}
                    {showRegisterButton && (
                      <motion.button
                        className="register-button"
                        onClick={() => { handleOpenContactModal(); }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        whileHover={{
                          scale: 1.05,
                          backgroundColor: 'var(--primary)',
                          color: '#000'
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {hero.buttonText || 'Register Now'}
                        <FaArrowRight className="button-icon" />
                      </motion.button>
                    )}
                  </div>
                </div>
              </div>

              {/* Full-width image */}
              {(hero.imageUrl || webinar.imageUrl) && (
                <motion.div
                  className="webinar-image-container"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                >
                  <LazyImage
                    src={hero.imageUrl || webinar.imageUrl}
                    alt={hero.heading || webinar.title}
                    className="webinar-image"
                    placeholder={<div className="webinar-image-placeholder" />}
                  />
                </motion.div>
              )}
            </div>
          </section>
        )}

        {/* ABOUT + AGENDA + SPEAKER + QUICK ACTIONS – v1 two-column layout, v2 data */}
        {(about.isVisible !== false ||
          (agenda.isVisible !== false && agendaItems.length > 0) ||
          speaker.isVisible !== false ||
          isUpcoming) && (
            <section className="two-column-layout">
              <div className="layout-container">
                <div className="main-grid">
                  {/* LEFT COLUMN */}
                  <div className="left-column">
                    {/* ABOUT / OVERVIEW */}
                    {about.isVisible !== false && (about.heading || about.subheading || (about.list && about.list.length > 0) || about.conclusion) && (
                      <motion.div
                        className="content-block"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                      >
                        {about.heading && <h2>{about.heading}</h2>}

                        {about.subheading && (
                          <p className="content-para">{about.subheading}</p>
                        )}

                        {about.listHeading && (
                          <p className="content-para content-list-heading">
                            {about.listHeading}
                          </p>
                        )}

                        {Array.isArray(about.list) && about.list.length > 0 && (
                          <ul className="content-list">
                            {about.list.map((item, index) => (
                              <motion.li
                                key={index}
                                initial={{ opacity: 0, x: -10 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                              >
                                {item}
                              </motion.li>
                            ))}
                          </ul>
                        )}

                        {about.conclusion && (
                          <p className="content-para">
                            {about.conclusion}
                          </p>
                        )}
                      </motion.div>
                    )}

                    {/* AGENDA / SCHEDULE */}
                    {agenda.isVisible !== false && agendaItems.length > 0 && (
                      <motion.div
                        className="schedule-block"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                      >
                        {agenda.Heading && <h3>{agenda.Heading}</h3>}
                        <div className="schedule-cards">
                          {agendaItems.map((item, index) => (
                            <motion.div
                              key={index}
                              className="schedule-card"
                              initial={{ opacity: 0, y: 10 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                            >
                              {item.time && (
                                <div className="time">{item.time}</div>
                              )}
                              <div className="event-text">
                                {item.title}
                                {item.length && (
                                  <span className="event-length"> &nbsp;•&nbsp; {item.length}</span>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {/* RIGHT COLUMN */}
                  <div className="right-column">
                    {/* SPEAKER CARD */}
                    {speaker.isVisible !== false && (speaker.name || speaker.companyPosition || speaker.imageUrl) && (
                      <motion.div
                        className="speaker-card"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                      >
                        <div className="speaker-header">
                          {speaker.imageUrl ? (
                            <div className="speaker-avatar">
                              <LazyImage
                                src={speaker.imageUrl}
                                alt={speaker.name}
                                className="speaker-avatar-image"
                                placeholder={<div className="speaker-avatar-placeholder" />}
                              />
                            </div>
                          ) : (
                            <FaUserTie className="speaker-icon" />
                          )}
                          <h4>{speaker.Heading || 'Featured Speaker'}</h4>
                        </div>
                        <div className="speaker-info">
                          {speaker.name && (
                            <div className="speaker-name">{speaker.name}</div>
                          )}
                          {speaker.companyPosition && (
                            <div className="speaker-title">{speaker.companyPosition}</div>
                          )}
                          {speaker.company && (
                            <div className="speaker-company">{speaker.company}</div>
                          )}
                        </div>

                        {/* Social Icons */}
                        {/* {speakerSocialLinks.length > 0 && (
                          <div className="speaker-socials">
                            {speakerSocialLinks.map((social, index) => (
                              <motion.a
                                key={index}
                                href={social.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="speaker-social-button"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                aria-label={social.icon || 'Social link'}
                              >
                                {getSocialIcon(social.icon)}
                              </motion.a>
                            ))}
                          </div>
                        )} */}
                      </motion.div>
                    )}

                    {/* QUICK ACTIONS */}
                    {isUpcoming && (
                      <motion.div
                        className="quick-actions"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                      >
                        <h4>{bottomSection.Heading || 'Quick Actions'}</h4>
                        <div className="action-buttons">
                          <motion.button
                            type="button"
                            className="action-button"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <FaCalendar />
                            Add to Calendar
                          </motion.button>

                          {showRegisterButton && (
                            <motion.button
                              onClick={() => { handleOpenContactModal(); }}
                              className="action-button action-button-primary"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <FaArrowRight />
                              {hero.buttonText || 'Register Now'}
                            </motion.button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>

                {/* FOOTER CONTENT BELOW TWO-COLUMN */}
                {(takeawaysList.length > 0 || bottomSection.Heading || bottomSection.Description) && (
                  <motion.div
                    className="footer-content"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                  >
                    {/* Bullet list from key takeaways (if any) */}
                    {takeawaysList.length > 0 && (
                      <ul className="footer-list">
                        {takeawaysList.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    )}

                    {/* Footer button */}
                    {showRegisterButton && (
                      <motion.a
                        onClick={() => { handleOpenContactModal(); }}
                        className="footer-button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {bottomSection.buttonText || 'Save Your Spot'}
                        <FaArrowRight className="button-arrow" />
                      </motion.a>
                    )}

                    {/* Footer paragraph */}
                    {(bottomSection.Description || about.conclusion) && (
                      <p className="footer-para">
                        {bottomSection.Description || about.conclusion}
                      </p>
                    )}

                    {/* Divider */}
                    <hr className="divider-line" />
                  </motion.div>
                )}
              </div>
            </section>
          )}

        {/* RELATED WEBINARS – dark cards */}
        <section className="related-webinars-section">
          <div className="related-container">
            {relatedConfig.Heading && (
              <h2 className="related-heading">
                {relatedConfig.Heading?.toUpperCase()}
              </h2>
            )}

            <div className="related-grid">
              {related.slice(0, 3).map((relatedWebinar) => (
                <Link
                  key={relatedWebinar._id}
                  to={`/webinars/${relatedWebinar.slug}`}
                  className="related-card"
                >
                  <h3 className="related-title">
                    {relatedWebinar.hero?.heading?.toUppercase || relatedWebinar.title}
                  </h3>
                  {relatedWebinar.dateTime && (
                    <p className="related-meta">
                      {formatDateTime(relatedWebinar.dateTime)}
                    </p>
                  )}
                  {relatedWebinar.shortDescription && (
                    <p className="related-excerpt">
                      {relatedWebinar.shortDescription}
                    </p>
                  )}
                  <span className="related-pill">
                    Learn More →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </article>
    </>
  )
}
